import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  activateUserPaidLicense,
  assignAdminUserToMe,
  cancelApprovalRequest,
  deactivateUserLicense,
  deleteAdminUser,
  fetchAdminUserDetail,
  resendApprovalRequest,
  startUserLicenseTrial,
  unassignAdminUser,
  updateAdminUser,
} from '../api/auth';
import GoToDashboardButton from './GoToDashboardButton';
import PasswordStrengthMeter, { getPasswordStrength } from './PasswordStrengthMeter';
import styles from './AdminUserDetailPage.module.css';

const emptyDraft = {
  username: '',
  email: '',
  password: '',
  fullName: '',
  company: '',
  department: '',
  plant: '',
  team: '',
  project: '',
  requestType: 'demo',
  licensePackage: 'evaluation',
  adminNote: '',
  role: 'normal',
  isActive: true,
  requiresLicenseAcceptance: false,
  onboardingCompletedAt: null,
  licenseState: 'not_required',
  licenseTrialStartedAt: null,
  licenseTrialExpiresAt: null,
  licenseActivatedAt: null,
  licenseExpiresAt: '',
  licenseDeactivationReason: '',
};

export default function AdminUserDetailPage({ currentUser }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isSuperadmin = currentUser?.role === 'admin' && currentUser?.isSuperadmin === true;
  const targetId = Number(id);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [resendingRequestId, setResendingRequestId] = useState(null);
  const [cancellingRequestId, setCancellingRequestId] = useState(null);
  const [licenseActionLoading, setLicenseActionLoading] = useState('');

  const loadDetail = useCallback(async () => {
    if (!Number.isInteger(targetId) || targetId <= 0) {
      setError(t('Invalid user id.'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchAdminUserDetail(targetId);
      setDetail(data);
      setDraft({
        username: data.user?.username || '',
        email: data.user?.email || '',
        password: '',
        fullName: data.user?.fullName || '',
        company: data.user?.company || '',
        department: data.user?.department || '',
        plant: data.user?.plant || '',
        team: data.user?.team || '',
        project: data.user?.project || '',
        requestType: data.user?.requestType || 'demo',
        licensePackage: data.user?.licensePackage || 'evaluation',
        adminNote: data.user?.adminNote || '',
        role: data.user?.role === 'admin' ? 'admin' : 'normal',
        isActive: Boolean(data.user?.isActive),
        requiresLicenseAcceptance: Boolean(data.user?.requiresLicenseAcceptance),
        onboardingCompletedAt: data.user?.onboardingCompletedAt || null,
        licenseState: data.user?.licenseState || 'not_required',
        licenseTrialStartedAt: data.user?.licenseTrialStartedAt || null,
        licenseTrialExpiresAt: data.user?.licenseTrialExpiresAt || null,
        licenseActivatedAt: data.user?.licenseActivatedAt || null,
        licenseExpiresAt: toDateInputValue(data.user?.licenseExpiresAt),
        licenseDeactivationReason: data.user?.licenseDeactivationReason || '',
      });
    } catch (err) {
      setError(err.message || t('Unable to load this user.'));
    } finally {
      setLoading(false);
    }
  }, [t, targetId]);

  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      navigate('/dashboard', { replace: true });
      return;
    }
    loadDetail();
  }, [currentUser?.role, loadDetail, navigate]);

  const timelineItems = useMemo(() => {
    const user = detail?.user;
    const latestApproval = detail?.approvalRequests?.[0];
    const latestAccessRequest = detail?.accessRequests?.[0];
    const latestActivity = detail?.activitySessions?.[0];
    return [
      { label: t('Created'), value: user?.createdAt },
      { label: t('Latest request'), value: latestAccessRequest?.createdAt },
      { label: t('Latest invitation'), value: latestApproval?.requestedAt },
      { label: t('Email verified'), value: user?.emailVerifiedAt },
      { label: t('Onboarding complete'), value: user?.onboardingCompletedAt },
      { label: t('Last seen'), value: latestActivity?.lastSeenAt },
      { label: t('Updated'), value: user?.updatedAt },
    ].filter((item) => item.value);
  }, [detail, t]);

  const canDelete = isSuperadmin && currentUser?.id !== detail?.user?.id;
  const passwordValid = !draft.password || getPasswordStrength(draft.password).isValid;
  const licenseAccess = detail?.licenseAccess || {};
  const assignedToCurrentAdmin = Number(detail?.user?.assignedAdminUserId) === Number(currentUser?.id);
  const assignedToAnotherAdmin = Number(detail?.user?.assignedAdminUserId) > 0 && !assignedToCurrentAdmin;
  const canAssignToMe = detail?.user && detail.user.id !== currentUser?.id && detail.user.role !== 'admin' && !assignedToCurrentAdmin && !assignedToAnotherAdmin;
  const canReleaseAssignment = detail?.user && detail.user.id !== currentUser?.id && detail.user.role !== 'admin' && (isSuperadmin || assignedToCurrentAdmin);

  const handleDraftChange = (key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await updateAdminUser(targetId, draft);
      setMessage(t('User saved successfully.'));
      await loadDetail();
    } catch (err) {
      setError(err.message || t('Unable to save this user.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      t('Delete user "{{username}}"? This cannot be undone.', {
        username: detail?.user?.username || '',
      })
    );
    if (!confirmed) return;

    setSaving(true);
    setMessage('');
    setError('');
    try {
      await deleteAdminUser(targetId);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err.message || t('Unable to delete this user.'));
    } finally {
      setSaving(false);
    }
  };

  const handleResendApproval = async (requestId) => {
    setResendingRequestId(requestId);
    setMessage('');
    setError('');
    try {
      const result = await resendApprovalRequest(requestId);
      setMessage(t(result.message || 'Approval email sent successfully.'));
      await loadDetail();
    } catch (err) {
      setError(err.message || t('Approval email could not be sent.'));
    } finally {
      setResendingRequestId(null);
    }
  };

  const handleCancelApproval = async (requestId) => {
    const confirmed = window.confirm(t('Cancel this invitation?'));
    if (!confirmed) return;
    setCancellingRequestId(requestId);
    setMessage('');
    setError('');
    try {
      const result = await cancelApprovalRequest(requestId);
      setMessage(t(result.message || 'Invitation cancelled successfully.'));
      await loadDetail();
    } catch (err) {
      setError(err.message || t('Unable to cancel this invitation.'));
    } finally {
      setCancellingRequestId(null);
    }
  };

  const handleStartTrial = async () => {
    setLicenseActionLoading('trial');
    setMessage('');
    setError('');
    try {
      const result = await startUserLicenseTrial(targetId);
      setMessage(t(result.message || 'A 7-day Smarti trial has been activated for this user.'));
      await loadDetail();
    } catch (err) {
      setError(err.message || t('Unable to start the trial.'));
    } finally {
      setLicenseActionLoading('');
    }
  };

  const handleActivatePaid = async () => {
    if (!draft.licenseExpiresAt) {
      setError(t('Choose a commercial expiration date first.'));
      return;
    }
    setLicenseActionLoading('activate');
    setMessage('');
    setError('');
    try {
      const result = await activateUserPaidLicense(targetId, new Date(`${draft.licenseExpiresAt}T23:59:59Z`).toISOString());
      setMessage(t(result.message || 'Commercial Smarti access is now active for this user.'));
      await loadDetail();
    } catch (err) {
      setError(err.message || t('Unable to activate commercial access.'));
    } finally {
      setLicenseActionLoading('');
    }
  };

  const handleDeactivateLicense = async () => {
    setLicenseActionLoading('deactivate');
    setMessage('');
    setError('');
    try {
      const result = await deactivateUserLicense(targetId, draft.licenseDeactivationReason || 'manually_deactivated');
      setMessage(t(result.message || 'License access has been deactivated for this user.'));
      await loadDetail();
    } catch (err) {
      setError(err.message || t('Unable to deactivate this license.'));
    } finally {
      setLicenseActionLoading('');
    }
  };

  const handleAssignToMe = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const result = await assignAdminUserToMe(targetId);
      setMessage(t(result.message || 'User assigned successfully.'));
      await loadDetail();
    } catch (err) {
      setError(err.message || t('Unable to assign this user.'));
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const result = await unassignAdminUser(targetId);
      setMessage(t(result.message || 'User assignment removed.'));
      await loadDetail();
    } catch (err) {
      setError(err.message || t('Unable to remove this assignment.'));
    } finally {
      setSaving(false);
    }
  };

  if (currentUser?.role !== 'admin') {
    return null;
  }

  return (
    <main className={styles.container}>
      <GoToDashboardButton />

      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>{t('Administration')}</p>
          <h1 className={styles.title}>{loading ? t('Loading user...') : detail?.user?.username || t('User detail')}</h1>
          <p className={styles.subtitle}>
            {t('Open one complete admin dossier for this account: identity, approval trail, license state, recent access sessions, and data footprint.')}
          </p>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.secondaryButton} onClick={() => navigate('/admin')}>
            {t('Back to Admin Panel')}
          </button>
          {detail?.user?.username ? (
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => navigate(`/admin/activity?username=${encodeURIComponent(detail.user.username)}`)}
            >
              {t('Open user activity')}
            </button>
          ) : null}
        </div>
      </header>

      {(message || error) ? (
        <div className={`${styles.notice} ${error ? styles.error : styles.success}`}>
          {error || message}
        </div>
      ) : null}

      {loading ? (
        <section className={styles.panel}>
          <div className={styles.emptyState}>{t('Loading user detail...')}</div>
        </section>
      ) : !detail?.user ? (
        <section className={styles.panel}>
          <div className={styles.emptyState}>{t('User not found.')}</div>
        </section>
      ) : (
        <>
          <section className={styles.metricsGrid}>
            <Metric label={t('Approval status')} value={approvalStatusLabel(detail.user.approvalStatus, t)} />
            <Metric label={t('Role')} value={detail.user.isSuperadmin ? t('Superadmin') : detail.user.role === 'admin' ? t('Admin') : t('Normal user')} />
            <Metric label={t('License state')} value={licenseStatusLabel(detail, t)} />
            <Metric label={t('Pending extensions')} value={String(detail.extensionRequests?.filter((request) => request.status === 'pending').length || 0)} />
            <Metric label={t('Production uploads')} value={String(detail.storage?.productionUploadBatches || 0)} />
            <Metric label={t('Production sessions')} value={String(detail.storage?.productionSessions || 0)} />
            <Metric label={t('Burn-in uploads')} value={String(detail.storage?.burnInUploads || 0)} />
          </section>

          <section className={styles.layout}>
            <div className={styles.mainColumn}>
              <section className={styles.panel}>
                <div className={styles.sectionHeader}>
                  <div>
                    <h2>{t('Account control')}</h2>
                    <p>{t('Edit the account directly from the detail page without going back to the card list.')}</p>
                  </div>
                </div>

                <div className={styles.formGrid}>
                  <Field label={t('Username')}>
                    <input
                      value={draft.username}
                      onChange={(event) => handleDraftChange('username', event.target.value)}
                    />
                  </Field>
                  <Field label={t('Email')}>
                    <input
                      type="email"
                      value={draft.email}
                      onChange={(event) => handleDraftChange('email', event.target.value)}
                    />
                  </Field>
                  <Field label={t('Full name')}>
                    <input
                      value={draft.fullName}
                      onChange={(event) => handleDraftChange('fullName', event.target.value)}
                    />
                  </Field>
                  <Field label={t('Company')}>
                    <input
                      value={draft.company}
                      onChange={(event) => handleDraftChange('company', event.target.value)}
                    />
                  </Field>
                  <Field label={t('Department')}>
                    <input
                      value={draft.department}
                      onChange={(event) => handleDraftChange('department', event.target.value)}
                    />
                  </Field>
                  <Field label={t('Plant')}>
                    <input
                      value={draft.plant}
                      onChange={(event) => handleDraftChange('plant', event.target.value)}
                    />
                  </Field>
                  <Field label={t('Team')}>
                    <input
                      value={draft.team}
                      onChange={(event) => handleDraftChange('team', event.target.value)}
                    />
                  </Field>
                  <Field label={t('Project')}>
                    <input
                      value={draft.project}
                      onChange={(event) => handleDraftChange('project', event.target.value)}
                    />
                  </Field>
                  <Field label={t('Request type')}>
                    <select
                      value={draft.requestType}
                      onChange={(event) => handleDraftChange('requestType', event.target.value)}
                    >
                      <option value="demo">{t('Demo')}</option>
                      <option value="pilot">{t('Pilot')}</option>
                      <option value="production">{t('Production use')}</option>
                    </select>
                  </Field>
                  <Field label={t('License package')}>
                    <select
                      value={draft.licensePackage}
                      onChange={(event) => handleDraftChange('licensePackage', event.target.value)}
                    >
                      <option value="evaluation">{t('Evaluation')}</option>
                      <option value="pilot">{t('Pilot')}</option>
                      <option value="production">{t('Production Candidate')}</option>
                    </select>
                  </Field>
                  <Field label={t('New password')}>
                    <input
                      type="password"
                      value={draft.password}
                      onChange={(event) => handleDraftChange('password', event.target.value)}
                      placeholder={t('leave blank to keep')}
                    />
                    {draft.password ? <PasswordStrengthMeter password={draft.password} compact /> : null}
                  </Field>
                  <Field label={t('Role')}>
                    <select
                      value={draft.role}
                      onChange={(event) => handleDraftChange('role', event.target.value)}
                      disabled={!isSuperadmin}
                    >
                      <option value="normal">{t('Normal user')}</option>
                      <option value="admin">{t('Admin')}</option>
                    </select>
                  </Field>
                  <label className={styles.toggleField}>
                    <input
                      type="checkbox"
                      checked={draft.isActive}
                      onChange={(event) => handleDraftChange('isActive', event.target.checked)}
                    />
                    {t('Active account')}
                  </label>
                  <label className={styles.toggleField}>
                    <input
                      type="checkbox"
                      checked={Boolean(draft.requiresLicenseAcceptance)}
                      disabled={draft.role === 'admin' || !isSuperadmin}
                      onChange={(event) => handleDraftChange('requiresLicenseAcceptance', event.target.checked)}
                    />
                    {t('Requires license agreement')}
                  </label>
                </div>

                <Field label={t('Internal admin note')}>
                  <textarea
                    value={draft.adminNote}
                    onChange={(event) => handleDraftChange('adminNote', event.target.value)}
                    rows={4}
                  />
                </Field>

                <div className={styles.metaRow}>
                  <span>{t('Created')}: {formatDate(detail.user.createdAt, i18n.language)}</span>
                  <span>{t('Email verified')}: {formatDate(detail.user.emailVerifiedAt, i18n.language)}</span>
                  <span>{t('Onboarding complete')}: {formatDate(detail.user.onboardingCompletedAt, i18n.language)}</span>
                  <span>{t('Updated')}: {formatDate(detail.user.updatedAt, i18n.language)}</span>
                  <span>{t('Assigned admin')}: {detail.user.assignedAdminUsername || '-'}</span>
                </div>

                <div className={styles.actionRow}>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={handleSave}
                    disabled={saving || !passwordValid}
                  >
                    {saving ? t('Saving...') : t('Save changes')}
                  </button>
                  {canAssignToMe ? (
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      onClick={handleAssignToMe}
                      disabled={saving}
                    >
                      {t('Assign to me')}
                    </button>
                  ) : null}
                  {canReleaseAssignment ? (
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      onClick={handleUnassign}
                      disabled={saving}
                    >
                      {t('Release')}
                    </button>
                  ) : null}
                  {isSuperadmin ? (
                    <button
                      type="button"
                      className={styles.dangerButton}
                      onClick={handleDelete}
                      disabled={saving || !canDelete}
                    >
                      {t('Delete user')}
                    </button>
                  ) : null}
                </div>
              </section>

              <section className={styles.panel}>
                <div className={styles.sectionHeader}>
                  <div>
                    <h2>{t('Ownership')}</h2>
                    <p>{t('Track which admin owns this working relationship inside the company account.')}</p>
                  </div>
                </div>
                <div className={styles.metaGrid}>
                  <Meta label={t('Assigned admin')} value={detail.user.assignedAdminUsername || '-'} />
                  <Meta label={t('Assigned admin id')} value={detail.user.assignedAdminUserId ? String(detail.user.assignedAdminUserId) : '-'} />
                  <Meta label={t('Role')} value={detail.user.isSuperadmin ? t('Superadmin') : detail.user.role === 'admin' ? t('Admin') : t('Normal user')} />
                </div>
              </section>

              <section className={styles.panel}>
                <div className={styles.sectionHeader}>
                  <div>
                    <h2>{t('Profile identity')}</h2>
                    <p>{t('See the full profile data the user entered on the dashboard selector, including the uploaded photo and contact context.')}</p>
                  </div>
                </div>

                <div className={styles.profileHero}>
                  {detail.user.avatarDataUrl ? (
                    <img src={detail.user.avatarDataUrl} alt={detail.user.fullName || detail.user.username || 'User avatar'} className={styles.avatarPreview} />
                  ) : (
                    <div className={styles.avatarFallback}>{buildAvatarInitials(detail.user)}</div>
                  )}
                  <div className={styles.profileIdentityStack}>
                    <strong>{detail.user.fullName || detail.user.username || '-'}</strong>
                    <span>{detail.user.profileTitle || t('No profile title')}</span>
                    <p>{detail.user.bio || t('No biography provided yet.')}</p>
                  </div>
                </div>

                <div className={styles.metaGrid}>
                  <Meta label={t('Phone')} value={detail.user.phone || '-'} />
                  <Meta label={t('Locale')} value={detail.user.locale || '-'} />
                  <Meta label={t('Timezone')} value={detail.user.timezone || '-'} />
                  <Meta label={t('Avatar updated')} value={formatDate(detail.user.avatarUpdatedAt, i18n.language)} />
                  <Meta label={t('Password changed')} value={formatDate(detail.user.lastPasswordChangedAt, i18n.language)} />
                  <Meta label={t('Profile title')} value={detail.user.profileTitle || '-'} />
                </div>
              </section>

              {isSuperadmin ? (
                <>
                  <section className={styles.panel}>
                    <div className={styles.sectionHeader}>
                      <div>
                        <h2>{t('Approval and request history')}</h2>
                        <p>{t('Review how this user entered Smarti and manage any still-open invitation emails directly from here.')}</p>
                      </div>
                    </div>

                    <div className={styles.historyStack}>
                      {detail.approvalRequests?.map((request) => (
                        <article key={`approval-${request.id}`} className={styles.historyCard}>
                          <div className={styles.historyTopline}>
                            <div>
                              <span className={`${styles.statusBadge} ${statusClassName(styles, request.status)}`}>
                                {approvalStatusLabel(request.status, t)}
                              </span>
                              <h3>{request.email}</h3>
                            </div>
                            <div className={styles.historyId}>#{request.id}</div>
                          </div>
                          <div className={styles.metaGrid}>
                            <Meta label={t('Requested')} value={formatDate(request.requestedAt, i18n.language)} />
                            <Meta label={t('Email sent')} value={formatDate(request.emailSentAt, i18n.language)} />
                            <Meta label={t('Expires')} value={formatDate(request.expiresAt, i18n.language)} />
                            <Meta label={t('Approved')} value={formatDate(request.approvedAt, i18n.language)} />
                            <Meta label={t('License required')} value={request.requiresLicenseAgreement ? t('Yes') : t('No')} />
                            <Meta label={t('Delivery')} value={request.deliveryProvider ? `${request.deliveryStatus} (${request.deliveryProvider})` : request.deliveryStatus} />
                          </div>
                          {request.deliveryError ? <div className={styles.detailNote}>{request.deliveryError}</div> : null}
                          {request.cancelledReason ? <div className={styles.detailNote}>{request.cancelledReason}</div> : null}
                          {request.status !== 'approved' ? (
                            <div className={styles.actionRow}>
                              <button
                                type="button"
                                className={styles.secondaryButton}
                                onClick={() => handleResendApproval(request.id)}
                                disabled={resendingRequestId === request.id || cancellingRequestId === request.id}
                              >
                                {resendingRequestId === request.id ? t('Sending...') : t('Resend invitation')}
                              </button>
                              <button
                                type="button"
                                className={styles.dangerButton}
                                onClick={() => handleCancelApproval(request.id)}
                                disabled={cancellingRequestId === request.id || resendingRequestId === request.id}
                              >
                                {cancellingRequestId === request.id ? t('Cancelling...') : t('Cancel invitation')}
                              </button>
                            </div>
                          ) : null}
                        </article>
                      ))}
                      {!detail.approvalRequests?.length ? (
                        <div className={styles.emptyState}>{t('No approval emails recorded for this user.')}</div>
                      ) : null}
                    </div>

                    <div className={styles.historyStack}>
                      {detail.accessRequests?.map((request) => (
                        <article key={`access-${request.id}`} className={styles.historyCard}>
                          <div className={styles.historyTopline}>
                            <div>
                              <span className={`${styles.statusBadge} ${statusClassName(styles, request.status)}`}>
                                {approvalStatusLabel(request.status, t)}
                              </span>
                              <h3>{request.requestedUsername}</h3>
                            </div>
                            <div className={styles.historyId}>#{request.id}</div>
                          </div>
                          <div className={styles.metaGrid}>
                            <Meta label={t('Email')} value={request.requestedEmail} />
                            <Meta label={t('Company')} value={request.company || '-'} />
                            <Meta label={t('Requested')} value={formatDate(request.createdAt, i18n.language)} />
                            <Meta label={t('Reviewed')} value={formatDate(request.reviewedAt, i18n.language)} />
                            <Meta label={t('Reviewed by')} value={request.reviewedByUsername || '-'} />
                            <Meta label={t('License required')} value={request.requireLicenseAgreement ? t('Yes') : t('No')} />
                          </div>
                          {request.note ? <div className={styles.detailNote}>{request.note}</div> : null}
                          {request.declineReason ? <div className={styles.detailNote}>{request.declineReason}</div> : null}
                        </article>
                      ))}
                      {!detail.accessRequests?.length ? (
                        <div className={styles.emptyState}>{t('No access requests are linked to this user.')}</div>
                      ) : null}
                    </div>
                  </section>

                  <section className={styles.panel}>
                    <div className={styles.sectionHeader}>
                      <div>
                        <h2>{t('Extension requests')}</h2>
                        <p>{t('Review when this user asked to prolong access, what plan they requested, and whether the notification email went out.')}</p>
                      </div>
                    </div>

                    <div className={styles.historyStack}>
                      {detail.extensionRequests?.map((request) => (
                        <article key={`extension-${request.id}`} className={styles.historyCard}>
                          <div className={styles.historyTopline}>
                            <div>
                              <span className={`${styles.statusBadge} ${statusClassName(styles, request.status)}`}>
                                {approvalStatusLabel(request.status, t)}
                              </span>
                              <h3>{request.requestedPlan || detail.user.licensePackage || '-'}</h3>
                            </div>
                            <div className={styles.historyId}>#{request.id}</div>
                          </div>
                          <div className={styles.metaGrid}>
                            <Meta label={t('Requested')} value={formatDate(request.requestedAt, i18n.language)} />
                            <Meta label={t('Current plan')} value={request.currentPlan || '-'} />
                            <Meta label={t('Current access')} value={request.currentAccessState || '-'} />
                            <Meta label={t('Current expiration')} value={formatDate(request.currentExpiresAt, i18n.language)} />
                            <Meta label={t('Email sent')} value={formatDate(request.emailSentAt, i18n.language)} />
                            <Meta label={t('Resolved by')} value={request.resolvedByUsername || '-'} />
                          </div>
                          {request.note ? <div className={styles.detailNote}>{request.note}</div> : null}
                          {request.emailDeliveryError ? <div className={styles.detailNote}>{request.emailDeliveryError}</div> : null}
                          {request.resolutionNote ? <div className={styles.detailNote}>{request.resolutionNote}</div> : null}
                        </article>
                      ))}
                      {!detail.extensionRequests?.length ? (
                        <div className={styles.emptyState}>{t('No extension requests are recorded for this user yet.')}</div>
                      ) : null}
                    </div>
                  </section>
                </>
              ) : null}
            </div>

            <div className={styles.sideColumn}>
              {isSuperadmin ? (
              <section className={styles.panel}>
                <div className={styles.sectionHeader}>
                  <div>
                    <h2>{t('Account timeline')}</h2>
                    <p>{t('See the main milestones for this account at a glance.')}</p>
                  </div>
                </div>
                <div className={styles.timelineList}>
                  {timelineItems.map((item) => (
                    <div key={item.label} className={styles.timelineItem}>
                      <span>{item.label}</span>
                      <strong>{formatDate(item.value, i18n.language)}</strong>
                    </div>
                  ))}
                </div>
              </section>
              ) : null}

              {isSuperadmin ? (
                <>
                  <section className={styles.panel}>
                    <div className={styles.sectionHeader}>
                      <div>
                        <h2>{t('Commercial license lifecycle')}</h2>
                        <p>{t('Trial, paid activation, and automatic expiry are controlled here. The signed license evidence remains below for audit.')}</p>
                      </div>
                    </div>

                    <div className={styles.metaGrid}>
                      <Meta label={t('Current state')} value={humanizeLicenseState(detail.user.licenseState, t)} />
                      <Meta label={t('Trial started')} value={formatDate(detail.user.licenseTrialStartedAt, i18n.language)} />
                      <Meta label={t('Trial ends')} value={formatDate(detail.user.licenseTrialExpiresAt, i18n.language)} />
                      <Meta label={t('Paid activated')} value={formatDate(detail.user.licenseActivatedAt, i18n.language)} />
                      <Meta label={t('Commercial expires')} value={formatDate(detail.user.licenseExpiresAt, i18n.language)} />
                      <Meta label={t('Blocked reason')} value={detail.user.licenseDeactivationReason || '-'} />
                    </div>

                    <div className={styles.formGrid}>
                      <Field label={t('Commercial expiration date')}>
                        <input
                          type="date"
                          value={draft.licenseExpiresAt}
                          onChange={(event) => handleDraftChange('licenseExpiresAt', event.target.value)}
                        />
                      </Field>
                      <Field label={t('Deactivation note')}>
                        <input
                          value={draft.licenseDeactivationReason}
                          onChange={(event) => handleDraftChange('licenseDeactivationReason', event.target.value)}
                          placeholder={t('trial_expired / paid_license_expired / manually_deactivated')}
                        />
                      </Field>
                    </div>

                    <div className={styles.detailNote}>
                      {licenseAccess.trialActive
                        ? t('This user is inside the 7-day trial and will be disabled automatically after the deadline unless you activate paid access.')
                        : licenseAccess.paidAccessActive
                        ? t('This user currently has active paid access until the expiration date shown above.')
                        : t('If license agreement is required, acceptance starts a 7-day trial. Paid access must then be activated from here.')}
                    </div>

                    <div className={styles.actionRow}>
                      <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={handleStartTrial}
                        disabled={licenseActionLoading !== '' || !detail.user.requiresLicenseAcceptance}
                      >
                        {licenseActionLoading === 'trial' ? t('Starting trial...') : t('Grant fresh 7-day trial')}
                      </button>
                      <button
                        type="button"
                        className={styles.primaryButton}
                        onClick={handleActivatePaid}
                        disabled={licenseActionLoading !== '' || !detail.user.requiresLicenseAcceptance}
                      >
                        {licenseActionLoading === 'activate' ? t('Activating...') : t('Activate paid access')}
                      </button>
                      <button
                        type="button"
                        className={styles.dangerButton}
                        onClick={handleDeactivateLicense}
                        disabled={licenseActionLoading !== '' || !detail.user.requiresLicenseAcceptance}
                      >
                        {licenseActionLoading === 'deactivate' ? t('Deactivating...') : t('Deactivate access')}
                      </button>
                    </div>
                  </section>

                  <section className={styles.panel}>
                    <div className={styles.sectionHeader}>
                      <div>
                        <h2>{t('License evidence')}</h2>
                        <p>{t('Track whether this user accepted the current Smarti terms and from which approval flow.')}</p>
                      </div>
                    </div>
                    {detail.licenseAcceptance ? (
                      <div className={styles.metaGrid}>
                        <Meta label={t('Accepted at')} value={formatDate(detail.licenseAcceptance.acceptedAt, i18n.language)} />
                        <Meta label={t('License version')} value={detail.licenseAcceptance.licenseVersion || '-'} />
                        <Meta label={t('Commercial terms')} value={detail.licenseAcceptance.commercialTermsVersion || '-'} />
                        <Meta label={t('Signed package')} value={detail.licenseAcceptance.licensePackage || '-'} />
                        <Meta label={t('Source')} value={detail.licenseAcceptance.acceptanceSource || '-'} />
                        <Meta label={t('Accepted IP')} value={detail.licenseAcceptance.acceptedIp || '-'} />
                        <Meta label={t('Request id')} value={detail.licenseAcceptance.requestId || '-'} />
                      </div>
                    ) : (
                      <div className={styles.emptyState}>{t('No accepted license record found for this account.')}</div>
                    )}
                  </section>
                </>
              ) : null}

              <section className={styles.panel}>
                <div className={styles.sectionHeader}>
                  <div>
                    <h2>{t('Data footprint')}</h2>
                    <p>{t('See whether this account already has production and burn-in data stored in its dedicated databases.')}</p>
                  </div>
                </div>
                <div className={styles.metaGrid}>
                  <Meta label={t('Production DB size')} value={formatBytes(detail.storage?.productionDatabaseBytes)} />
                  <Meta label={t('Burn-in DB size')} value={formatBytes(detail.storage?.burnInDatabaseBytes)} />
                  <Meta label={t('Production step rows')} value={String(detail.storage?.productionStepRows || 0)} />
                  <Meta label={t('Burn-in measurements')} value={String(detail.storage?.burnInMeasurements || 0)} />
                  <Meta label={t('Production DB exists')} value={detail.storage?.productionDatabaseExists ? t('Yes') : t('No')} />
                  <Meta label={t('Burn-in DB exists')} value={detail.storage?.burnInDatabaseExists ? t('Yes') : t('No')} />
                </div>
              </section>

              <section className={styles.panel}>
                <div className={styles.sectionHeader}>
                  <div>
                    <h2>{t('Recent access sessions')}</h2>
                    <p>{t('Inspect the latest connections for this user without leaving the detail page.')}</p>
                  </div>
                </div>
                <div className={styles.historyStack}>
                  {detail.activitySessions?.map((session) => (
                    <article key={session.id} className={styles.historyCard}>
                      <div className={styles.historyTopline}>
                        <div>
                          <span className={`${styles.statusBadge} ${session.disconnectedAt ? styles.expiredBadge : styles.approvedBadge}`}>
                            {session.disconnectedAt ? t('Disconnected') : t('Still connected')}
                          </span>
                          <h3>{session.ipAddress || '—'}</h3>
                        </div>
                        <div className={styles.historyId}>#{session.id}</div>
                      </div>
                      <div className={styles.metaGrid}>
                        <Meta label={t('Connected')} value={formatDate(session.connectedAt, i18n.language)} />
                        <Meta label={t('Last seen')} value={formatDate(session.lastSeenAt, i18n.language)} />
                        <Meta label={t('Location')} value={session.location || '-'} />
                        <Meta label={t('Browser')} value={session.browser || '-'} />
                        <Meta label={t('OS')} value={session.os || '-'} />
                        <Meta label={t('Visits')} value={String(session.pageVisitCount || 0)} />
                      </div>
                      {session.lastPath ? <div className={styles.detailNote}>{session.lastPath}</div> : null}
                    </article>
                  ))}
                  {!detail.activitySessions?.length ? (
                    <div className={styles.emptyState}>{t('No access sessions recorded yet for this user.')}</div>
                  ) : null}
                </div>
              </section>
            </div>
          </section>
        </>
      )}
    </main>
  );
}

function Metric({ label, value }) {
  return (
    <div className={styles.metricCard}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className={styles.field}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function Meta({ label, value }) {
  return (
    <div className={styles.metaBox}>
      <dt>{label}</dt>
      <dd>{value || '-'}</dd>
    </div>
  );
}

function formatDate(value, locale) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale || undefined);
}

function toDateInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function formatBytes(value) {
  const bytes = Number(value) || 0;
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const amount = bytes / (1024 ** power);
  return `${amount >= 10 || power === 0 ? amount.toFixed(0) : amount.toFixed(1)} ${units[power]}`;
}

function buildAvatarInitials(user = {}) {
  const base = user.fullName || user.username || 'Smarti User';
  return base
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'SU';
}

function approvalStatusLabel(status, t) {
  switch (status) {
    case 'pending':
      return t('Pending approval');
    case 'approved':
      return t('Approved');
    case 'declined':
      return t('Declined');
    case 'expired':
      return t('Expired');
    case 'cancelled':
      return t('Cancelled');
    case 'superseded':
      return t('Superseded');
    case 'email_failed':
      return t('Email failed');
    default:
      return t(status || 'Approved');
  }
}

function licenseStatusLabel(detail, t) {
  if (detail?.user?.requiresLicenseAcceptance) {
    return humanizeLicenseState(detail?.user?.licenseState, t);
  }
  if (detail?.licenseAcceptance) return t('Accepted');
  if (detail?.user?.role === 'admin') return t('Exempt');
  return t('Not required');
}

function humanizeLicenseState(state, t) {
  switch (state) {
    case 'pending_acceptance':
      return t('Pending acceptance');
    case 'trial':
      return t('Trial');
    case 'active':
      return t('Paid active');
    case 'expired':
      return t('Expired');
    case 'not_required':
    default:
      return t('Not required');
  }
}

function statusClassName(stylesObject, status) {
  switch (status) {
    case 'approved':
      return stylesObject.approvedBadge;
    case 'pending':
      return stylesObject.pendingBadge;
    default:
      return stylesObject.expiredBadge;
  }
}
